import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { listPublicOffers } from "@/lib/public.functions";

const SESSION_KEY = "lbdc-promo-popup-seen";

export function PromoPopup() {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["public-offers"],
    queryFn: listPublicOffers,
    staleTime: 60_000,
  });
  const offer = data?.offers[0];

  useEffect(() => {
    if (!offer || window.sessionStorage.getItem(SESSION_KEY)) return;
    setOpen(true);
    window.sessionStorage.setItem(SESSION_KEY, "1");
  }, [offer]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!open || !offer) return null;

  const close = () => setOpen(false);
  const hasCopy = Boolean(offer.title || offer.description || offer.cta_text);
  const imageOnly = !hasCopy && !!offer.image;
  const alt = offer.title || "Offerta della Bottega";

  const ImageEl = offer.image ? (
    offer.cta_link ? (
      <a
        href={offer.cta_link}
        aria-label={offer.title ? `Vai a: ${offer.title}` : "Vai all'offerta"}
        onClick={close}
        className="block cursor-pointer"
      >
        <img
          src={offer.image}
          alt={alt}
          className={`mx-auto block max-h-[80vh] w-auto max-w-full object-contain ${imageOnly ? "" : "bg-brand-cream"}`}
        />
      </a>
    ) : (
      <img
        src={offer.image}
        alt={alt}
        className={`mx-auto block max-h-[80vh] w-auto max-w-full object-contain ${imageOnly ? "" : "bg-brand-cream"}`}
      />
    )
  ) : null;

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-brand-charcoal/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={offer.title || "Offerta in evidenza"}
      onClick={close}
    >
      {imageOnly ? (
        <div className="relative max-h-[90vh] max-w-[95vw]" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={close}
            aria-label="Chiudi offerta"
            className="absolute right-2 top-2 z-10 grid h-10 w-10 place-items-center rounded-full bg-black/60 text-white shadow-md backdrop-blur-sm transition hover:bg-black/80"
          >
            <X className="h-5 w-5" />
          </button>
          {ImageEl}
        </div>
      ) : (
        <div
          className="relative w-full max-w-xl overflow-hidden rounded-md bg-brand-cream text-brand-charcoal shadow-[var(--shadow-hover)]"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Chiudi offerta"
            className="absolute right-2 top-2 z-10 grid h-10 w-10 place-items-center rounded-full bg-black/60 text-white shadow-md backdrop-blur-sm transition hover:bg-black/80"
          >
            <X className="h-5 w-5" />
          </button>
          {ImageEl}
          {hasCopy && (
            <div className="p-6 text-center sm:p-8">
              {offer.title && <h2 className="font-display text-2xl sm:text-3xl">{offer.title}</h2>}
              {offer.description && <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">{offer.description}</p>}
              {offer.cta_text && offer.cta_link && (
                <Button asChild className="mt-5 bg-brand-red hover:bg-brand-red-hover" onClick={close}>
                  <a href={offer.cta_link}>{offer.cta_text}</a>
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}